import { authenticatedRequest } from './base.service';

// ==================== CONFIG CHANGE APPROVAL ====================

export type OrgConfigParamType =
  | 'credit_settings'
  | 'transaction_limits'
  | 'payroll_config';

export type OrgConfigChangeStatus = 'pending' | 'approved' | 'rejected';

export interface OrgConfigChange {
  id: string;
  organizationId: string;
  paramType: OrgConfigParamType;
  payload: Record<string, unknown>;
  status: OrgConfigChangeStatus;
  proposedBy: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ==================== CREDIT SETTINGS ====================

export type RatePeriod = 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface CreditSettings {
  creditInterestRate: number;
  period: RatePeriod;
  creditTermDays: number;
  creditMaxAmount: number | null;
  creditMaxSalaryPercentage: number;
  isConfigured: boolean;
}

export interface CreditSettingsStatus {
  isConfigured: boolean;
  hasRates: boolean;
  hasOrgSettings: boolean;
  missingFields: string[];
  warnings: string[];
}

export interface UpdateCreditSettingsData {
  creditInterestRate?: number;
  period?: RatePeriod;
  creditTermDays?: number;
  creditMaxAmount?: number | null;
  creditMaxSalaryPercentage?: number;
}

// ==================== TRANSACTION LIMITS ====================

export interface TransactionLimits {
  dailyTransactionLimit: number | null;
  monthlyTransactionLimit: number | null;
  transactionLimitPerOperation: number | null;
  isConfigured: boolean;
}

export interface TransactionLimitsStatus {
  isConfigured: boolean;
  missingFields: string[];
  configuredFields: string[];
  warnings: string[];
}

export interface UpdateTransactionLimitsData {
  dailyTransactionLimit?: number | null;
  monthlyTransactionLimit?: number | null;
  transactionLimitPerOperation?: number | null;
}

// ==================== PAYROLL CONFIG ====================

export interface PayrollConfig {
  payrollFrequency: 'biweekly' | 'monthly';
  payrollDayOfMonth1: number;
  payrollDayOfMonth2: number | null;
  payrollAutoDispersion: boolean;
  payrollDispersionTime: string;
  isConfigured: boolean;
}

export interface PayrollConfigStatus {
  isConfigured: boolean;
  missingFields: string[];
  configuredFields: string[];
  warnings: string[];
  usingDefaults: boolean;
}

export interface UpdatePayrollConfigData {
  payrollFrequency?: 'biweekly' | 'monthly';
  payrollDayOfMonth1?: number;
  payrollDayOfMonth2?: number | null;
  payrollAutoDispersion?: boolean;
  payrollDispersionTime?: string;
}

// ==================== PAYROLL PAYABLE SUMMARY ====================

export interface PayableEmployee {
  userId: string;
  name: string;
  gross: number;
  creditDeduction: number;
  net: number;
}

export interface PayableSummary {
  payDate: string;
  periodStart: string;
  periodEnd: string;
  totalGross: number;
  totalDeductions: number;
  totalNet: number;
  employeeCount: number;
  employees: PayableEmployee[];
}

const getToken = (): string => {
  const token = localStorage.getItem('finara_token');
  if (!token) throw new Error('No authentication token found');
  return token;
};

export const organizationConfigService = {
  // ==================== CREDIT SETTINGS ====================
  
  async getCreditSettings(): Promise<CreditSettings> {
    return authenticatedRequest<CreditSettings>(
      '/api/v1/organizations/credit-settings',
      {},
      getToken()
    );
  },

  async updateCreditSettings(data: UpdateCreditSettingsData): Promise<OrgConfigChange> {
    return authenticatedRequest<OrgConfigChange>(
      '/api/v1/organizations/credit-settings',
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      getToken()
    );
  },

  async getCreditSettingsStatus(): Promise<CreditSettingsStatus> {
    return authenticatedRequest<CreditSettingsStatus>(
      '/api/v1/organizations/credit-settings/status',
      {},
      getToken()
    );
  },

  // ==================== TRANSACTION LIMITS ====================

  async getTransactionLimits(): Promise<TransactionLimits> {
    return authenticatedRequest<TransactionLimits>(
      '/api/v1/organizations/transaction-limits',
      {},
      getToken()
    );
  },

  async updateTransactionLimits(data: UpdateTransactionLimitsData): Promise<OrgConfigChange> {
    return authenticatedRequest<OrgConfigChange>(
      '/api/v1/organizations/transaction-limits',
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      getToken()
    );
  },

  async getTransactionLimitsStatus(): Promise<TransactionLimitsStatus> {
    return authenticatedRequest<TransactionLimitsStatus>(
      '/api/v1/organizations/transaction-limits/status',
      {},
      getToken()
    );
  },

  // ==================== PAYROLL CONFIG ====================

  async getPayrollConfig(): Promise<PayrollConfig> {
    return authenticatedRequest<PayrollConfig>(
      '/api/v1/organizations/payroll-config',
      {},
      getToken()
    );
  },

  async updatePayrollConfig(data: UpdatePayrollConfigData): Promise<OrgConfigChange> {
    return authenticatedRequest<OrgConfigChange>(
      '/api/v1/organizations/payroll-config',
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      getToken()
    );
  },

  async getPayrollConfigStatus(): Promise<PayrollConfigStatus> {
    return authenticatedRequest<PayrollConfigStatus>(
      '/api/v1/organizations/payroll-config/status',
      {},
      getToken()
    );
  },

  // ==================== PAYROLL PAYABLE SUMMARY ====================

  async getPayableSummary(payDate?: string): Promise<PayableSummary> {
    const query = payDate ? `?payDate=${payDate}` : '';
    return authenticatedRequest<PayableSummary>(
      `/api/v1/payroll/payable-summary${query}`,
      {},
      getToken()
    );
  },

  // ==================== CONFIG CHANGE APPROVAL (super admin) ====================

  async listConfigChanges(params?: {
    status?: OrgConfigChangeStatus;
    organizationId?: string;
  }): Promise<OrgConfigChange[]> {
    const qs = new URLSearchParams();
    if (params?.status) qs.append('status', params.status);
    if (params?.organizationId) qs.append('organizationId', params.organizationId);
    const query = qs.toString();
    return authenticatedRequest<OrgConfigChange[]>(
      `/api/v1/organizations/super-admin/config-changes${query ? `?${query}` : ''}`,
      {},
      getToken()
    );
  },

  async approveConfigChange(id: string): Promise<OrgConfigChange> {
    return authenticatedRequest<OrgConfigChange>(
      `/api/v1/organizations/super-admin/config-changes/${id}/approve`,
      { method: 'POST' },
      getToken()
    );
  },

  async rejectConfigChange(id: string): Promise<OrgConfigChange> {
    return authenticatedRequest<OrgConfigChange>(
      `/api/v1/organizations/super-admin/config-changes/${id}/reject`,
      { method: 'POST' },
      getToken()
    );
  },

  // ==================== SUPER-ADMIN PER-ORG CONFIG ====================

  async getOrgCreditSettings(organizationId: string): Promise<CreditSettings> {
    return authenticatedRequest<CreditSettings>(
      `/api/v1/organizations/${organizationId}/credit-settings`,
      {},
      getToken()
    );
  },

  async getOrgTransactionLimits(organizationId: string): Promise<TransactionLimits> {
    return authenticatedRequest<TransactionLimits>(
      `/api/v1/organizations/${organizationId}/transaction-limits`,
      {},
      getToken()
    );
  },

  async getOrgPayrollConfig(organizationId: string): Promise<PayrollConfig> {
    return authenticatedRequest<PayrollConfig>(
      `/api/v1/organizations/${organizationId}/payroll-config`,
      {},
      getToken()
    );
  },

  async proposeOrgCreditSettings(
    organizationId: string,
    data: UpdateCreditSettingsData,
    autoApprove = false
  ): Promise<OrgConfigChange> {
    return authenticatedRequest<OrgConfigChange>(
      `/api/v1/organizations/${organizationId}/credit-settings?autoApprove=${autoApprove}`,
      { method: 'PATCH', body: JSON.stringify(data) },
      getToken()
    );
  },

  async proposeOrgTransactionLimits(
    organizationId: string,
    data: UpdateTransactionLimitsData,
    autoApprove = false
  ): Promise<OrgConfigChange> {
    return authenticatedRequest<OrgConfigChange>(
      `/api/v1/organizations/${organizationId}/transaction-limits?autoApprove=${autoApprove}`,
      { method: 'PATCH', body: JSON.stringify(data) },
      getToken()
    );
  },

  async proposeOrgPayrollConfig(
    organizationId: string,
    data: UpdatePayrollConfigData,
    autoApprove = false
  ): Promise<OrgConfigChange> {
    return authenticatedRequest<OrgConfigChange>(
      `/api/v1/organizations/${organizationId}/payroll-config?autoApprove=${autoApprove}`,
      { method: 'PATCH', body: JSON.stringify(data) },
      getToken()
    );
  },
};
